import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import RouteMap from "../../components/RouteMap";
import {
  Typography,
  Paper,
  Grid,
  Box,
  Chip,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  Snackbar,
  Alert,
  Menu,
  ListItemIcon,
  ListItemButton,
  Checkbox,
  Badge,
  Avatar,
  Fab,
  CircularProgress,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Route as RouteIcon,
  Person as PersonIcon,
  Assignment as OrderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
  DirectionsCar as CarIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  LocationOn as LocationIcon,
  MoreVert as MoreVertIcon,
  SwapVert as SwapVertIcon,
  Save as SaveIcon,
  Cancel as CancelActionIcon,
  Map as MapIcon,
  DragIndicator as DragIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Reorder as ReorderIcon,
  Speed as OptimizedIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { routeService } from "../../services/routeService";
import { orderService } from "../../services/orderService";
import { userService } from "../../services/userService";

const RouteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [route, setRoute] = useState(null);
  const [orders, setOrders] = useState([]);
  const [medicalProfessionals, setMedicalProfessionals] = useState([]);
  const [selectedMP, setSelectedMP] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);

  // Enhanced editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editedRouteName, setEditedRouteName] = useState("");

  // Schedule editing states
  const [editedScheduleDate, setEditedScheduleDate] = useState("");
  const [editedStartTime, setEditedStartTime] = useState("");
  const [editedEndTime, setEditedEndTime] = useState("");
  const [scheduleEditMode, setScheduleEditMode] = useState(false);

  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [availableOrders, setAvailableOrders] = useState([]);
  const [selectedAvailableOrders, setSelectedAvailableOrders] = useState([]);
  const [orderActionsAnchorEl, setOrderActionsAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openAddOrdersDialog, setOpenAddOrdersDialog] = useState(false);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [reorderedOrders, setReorderedOrders] = useState([]);

  // Medical professional info
  const [mpStartingPoint, setMpStartingPoint] = useState(null);

  useEffect(() => {
    fetchRouteData();
  }, [id]);

  const fetchRouteData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch route details
      const routeResponse = await routeService.getRouteById(id);
      setRoute(routeResponse);
      setEditedRouteName(routeResponse.name);

      // Set schedule editing values
      setEditedScheduleDate(routeResponse.scheduled_date);
      setEditedStartTime(routeResponse.scheduled_time_start || "08:00");
      setEditedEndTime(routeResponse.scheduled_time_end || "17:00");

      // Fetch orders for this route
      const ordersResponse = await routeService.getRouteOrders(id);
      setOrders(ordersResponse || []);
      setReorderedOrders(ordersResponse || []);

      const mpsResponse =
        await routeService.getMedicalProfessionalsWithStartingPoints();
      setMedicalProfessionals(mpsResponse || []);

      // Set selected medical professional if already assigned
      if (routeResponse.medical_professional_id) {
        setSelectedMP(routeResponse.medical_professional_id);

        const mp = mpsResponse.find(
          (mp) => mp.id === routeResponse.medical_professional_id
        );

        if (mp?.starting_location) {
          setMpStartingPoint(mp.starting_location);
        }
      }
    } catch (err) {
      console.error("Error fetching route data:", err);
      setError("Error fetching route data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== DRAG AND DROP HANDLERS =====

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (result) => {
    setIsDragging(false);

    if (!result.destination) {
      return;
    }

    // Don't do anything if dropped in same position
    if (result.source.index === result.destination.index) {
      return;
    }

    const items = Array.from(reorderedOrders);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for responsive UI
    setReorderedOrders(items);

    // Auto-save the new order to backend
    handleSaveReorder(items);
  };

  const handleSaveReorder = async (newOrderList = reorderedOrders) => {
    try {
      setSaving(true);
      setError(null);

      const orderIds = newOrderList.map((order) => order.id);

      // Call the backend to reorder
      await routeService.reorderRouteOrders(id, { order_ids: orderIds });

      // Update local state AFTER successful save
      setOrders(newOrderList);
      setReorderedOrders(newOrderList);

      setSuccess("Route order updated successfully!");

      // Optional: refresh after a delay if no other changes are being made
      setTimeout(() => {
        if (!isDragging && !saving) {
          fetchRouteData();
        }
      }, 3000);
    } catch (err) {
      console.error("Error reordering route:", err);
      setError("Failed to save new order: " + err.message);

      // Revert the reorder on error
      setReorderedOrders(orders);

      // Show user-friendly error messages
      if (err.message.includes("not found")) {
        setError(
          "Some orders could not be found. Please refresh and try again."
        );
      } else if (
        err.message.includes("permission") ||
        err.message.includes("authorized")
      ) {
        setError("You don't have permission to modify this route.");
      }
    } finally {
      setSaving(false);
    }
  };

  // ===== MAPS INTEGRATION =====

  const handleOpenInMaps = async () => {
    try {
      if (orders.length === 0) {
        setError("No orders in this route to display in maps");
        return;
      }


      const mps =
        await routeService.getMedicalProfessionalsWithStartingPoints();

      const mp = mps.find((mp) => mp.id === route.medical_professional_id);

      let startPoint = "Main Medical Center, Los Angeles, CA";

      if (mp) {

        if (mp.starting_location) {

          if (mp.starting_location.lat && mp.starting_location.lng) {
            startPoint = `${mp.starting_location.lat},${mp.starting_location.lng}`;
          } else if (mp.starting_location.address) {
            startPoint = mp.starting_location.address;
          } else if (mp.starting_location.name) {
            startPoint = mp.starting_location.name;
          }
        } else {
        }
      } else {
      }


      // Get valid addresses from orders
      const validAddresses = orders
        .map((order) => order.patient_address)
        .filter(Boolean);


      if (validAddresses.length === 0) {
        setError("No valid addresses found in route orders");
        return;
      }

      // Build Google Maps URL
      const origin = encodeURIComponent(startPoint);

      let mapsUrl;
      if (validAddresses.length === 1) {
        const destination = encodeURIComponent(validAddresses[0]);
        mapsUrl = `https://www.google.com/maps/dir/${origin}/${destination}`;
      } else {
        const destinations = validAddresses
          .map((addr) => encodeURIComponent(addr))
          .join("/");
        mapsUrl = `https://www.google.com/maps/dir/${origin}/${destinations}`;
      }


      window.open(mapsUrl, "_blank");
      setSuccess("Opened route in Google Maps!");
    } catch (err) {
      console.error("Error opening maps:", err);
      setError("Failed to open route in Google Maps: " + err.message);
    }
  };

  const handleGetDirections = async () => {
    try {
      const mapsUrl = await routeService.generateMapsUrl(id, true);

      // Show the URL to user and open it
      window.open(mapsUrl, "_blank");
      setSuccess("Opened route directions in Google Maps!");
    } catch (err) {
      console.error("Error getting directions:", err);
      setError("Failed to get directions: " + err.message);
    }
  };

  // Fetch available orders that could be added to this route
  const fetchAvailableOrders = async () => {
    if (!route) return;

    try {
      // Get orders for the same date that aren't assigned to a route yet
      const response = await orderService.getAllOrders({
        scheduled_date: route.scheduled_date,
        status: "assigned", // Get assigned orders not yet in routes
      });

      // Filter out orders that are already in this route
      const routeOrderIds = orders.map((order) => order.id);
      const filteredOrders = response.filter(
        (order) =>
          !routeOrderIds.includes(order.id) &&
          (!order.route_group_id || order.route_group_id === null)
      );

      setAvailableOrders(filteredOrders);
    } catch (err) {
      console.error("Error fetching available orders:", err);
      setError("Failed to load available orders");
    }
  };

  const handleAssignMP = async () => {
    if (!selectedMP) {
      setError("Please select a medical professional");
      return;
    }

    try {
      setIsUpdating(true);
      setError(null);

      await routeService.assignMedicalProfessional(id, {
        medical_professional_id: selectedMP,
      });

      setSuccess("Medical professional assigned successfully");

      // Refresh route data to show the updated assignment
      await fetchRouteData();
    } catch (err) {
      console.error("Error assigning medical professional:", err);
      setError("Error assigning medical professional: " + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      setIsUpdating(true);
      setOpenDialog(false);
      setError(null);

      await routeService.updateRouteStatus(id, {
        status: newStatus,
        notes: `Status updated to ${newStatus}`,
      });

      setSuccess(`Route status updated to ${newStatus}`);

      // Refresh route data to show the updated status
      await fetchRouteData();
    } catch (err) {
      console.error("Error updating route status:", err);
      setError("Error updating route status: " + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const openConfirmationDialog = (action, status) => {
    setDialogAction({ action, status });
    setOpenDialog(true);
  };

  const getStatusChip = (status) => {
    let color = "default";
    switch (status) {
      case "completed":
        color = "success";
        break;
      case "in_progress":
        color = "primary";
        break;
      case "assigned":
        color = "info";
        break;
      case "created":
        color = "warning";
        break;
      case "cancelled":
        color = "error";
        break;
      default:
        color = "default";
    }

    return <Chip label={status.replace("_", " ")} color={color} size="small" />;
  };

  // Enter edit mode for route
  const handleStartEdit = () => {
    setIsEditing(true);
    fetchAvailableOrders();
  };

  // Cancel editing and revert changes
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedRouteName(route.name);
    setEditedScheduleDate(route.scheduled_date);
    setEditedStartTime(route.scheduled_time_start || "08:00");
    setEditedEndTime(route.scheduled_time_end || "17:00");
    setScheduleEditMode(false);
    setSelectedAvailableOrders([]);
    setReorderedOrders(orders);
  };

  // Save route changes including schedule
  const handleSaveRouteChanges = async () => {
    setSaving(true);

    try {
      const updateData = {};
      let hasChanges = false;

      // Check for name changes
      if (editedRouteName !== route.name) {
        updateData.name = editedRouteName;
        hasChanges = true;
      }

      // Check for schedule changes
      if (editedScheduleDate !== route.scheduled_date) {
        updateData.scheduled_date = editedScheduleDate;
        hasChanges = true;
      }

      if (editedStartTime !== (route.scheduled_time_start || "08:00")) {
        updateData.scheduled_time_start = editedStartTime;
        hasChanges = true;
      }

      if (editedEndTime !== (route.scheduled_time_end || "17:00")) {
        updateData.scheduled_time_end = editedEndTime;
        hasChanges = true;
      }

      // Update route if there are changes
      if (hasChanges) {
        await routeService.updateRoute(id, updateData);
      }

      // Add selected orders to route
      if (selectedAvailableOrders.length > 0) {
        await routeService.addOrdersToRoute(id, {
          order_ids: selectedAvailableOrders,
        });
      }

      setSuccess("Route updated successfully");
      setIsEditing(false);
      setScheduleEditMode(false);
      setSelectedAvailableOrders([]);
      await fetchRouteData();
    } catch (err) {
      console.error("Error saving route changes:", err);
      setError("Failed to save route changes: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle order menu open
  const handleOrderMenuOpen = (event, order) => {
    setOrderActionsAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  // Handle order menu close
  const handleOrderMenuClose = () => {
    setOrderActionsAnchorEl(null);
    setSelectedOrder(null);
  };

  // Remove order from route
  const handleRemoveOrderFromRoute = async () => {
    if (!selectedOrder) return;

    handleOrderMenuClose();

    try {
      setIsUpdating(true);
      await routeService.removeOrderFromRoute(id, selectedOrder.id);
      setSuccess(`Order ${selectedOrder.order_number} removed from route`);
      await fetchRouteData();
    } catch (err) {
      console.error("Error removing order from route:", err);
      setError("Failed to remove order from route");
    } finally {
      setIsUpdating(false);
    }
  };

  // Toggle select available order
  const handleToggleSelectOrder = (orderId) => {
    setSelectedAvailableOrders((prev) => {
      if (prev.includes(orderId)) {
        return prev.filter((id) => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  // Filter available orders by search query
  const filteredAvailableOrders = availableOrders.filter((order) => {
    const orderNumber = order.order_number || "";
    const patientName =
      order.patient?.first_name + " " + order.patient?.last_name || "";
    const address = order.patient?.address || "";

    return (
      orderNumber.toLowerCase().includes(orderSearchQuery.toLowerCase()) ||
      patientName.toLowerCase().includes(orderSearchQuery.toLowerCase()) ||
      address.toLowerCase().includes(orderSearchQuery.toLowerCase())
    );
  });

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return "Not set";

    // Handle time strings like "08:00:00" or "08:00"
    const timeParts = timeString.split(":");
    if (timeParts.length >= 2) {
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    return timeString;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    // Force local time interpretation to avoid timezone shifts
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString();
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (error && !route) {
    return (
      <Box textAlign="center" my={5}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/routes")}
          sx={{ mt: 2 }}
        >
          Back to Routes
        </Button>
      </Box>
    );
  }

  if (!route) {
    return (
      <Box textAlign="center" my={5}>
        <Typography variant="h6">Route not found</Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/routes")}
          sx={{ mt: 2 }}
        >
          Back to Routes
        </Button>
      </Box>
    );
  }

  const assignedMP = medicalProfessionals.find(
    (mp) => mp.id === route.medical_professional_id
  );

  return (
    <Box>
      {/* Success notification */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate("/routes")}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          {isEditing ? (
            <TextField
              value={editedRouteName}
              onChange={(e) => setEditedRouteName(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ minWidth: 300 }}
            />
          ) : (
            <Typography variant="h4">{route.name}</Typography>
          )}
        </Box>
        <Box>
          {/* Maps Integration Buttons */}
          <Button
            variant="outlined"
            startIcon={<MapIcon />}
            onClick={handleOpenInMaps}
            sx={{ mr: 1 }}
            disabled={orders.length === 0}
          >
            Open Maps
          </Button>
          <Button
            variant="outlined"
            startIcon={<CarIcon />}
            onClick={handleGetDirections}
            sx={{ mr: 1 }}
            disabled={orders.length === 0}
          >
            Directions
          </Button>

          {isEditing ? (
            <>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CancelActionIcon />}
                sx={{ ml: 1 }}
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                sx={{ ml: 1 }}
                onClick={handleSaveRouteChanges}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </>
          ) : (
            route.status !== "completed" &&
            route.status !== "cancelled" && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                sx={{ ml: 1 }}
                onClick={handleStartEdit}
              >
                Edit Route
              </Button>
            )
          )}
        </Box>
      </Box>

      {error && (
        <Box mb={3}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}

      {/* Route Info Bar - Compact stats */}
      <Paper elevation={0} sx={{ p: isMobile ? 2 : 2.5, mb: 3, border: "1px solid #E2E8F0", borderRadius: 3 }}>
        <Grid container spacing={isMobile ? 1.5 : 3} alignItems="center">
          <Grid  size={{ xs: 6, sm: 4, md: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.05em" }}>
              Status
            </Typography>
            <Box mt={0.5}>{getStatusChip(route.status)}</Box>
          </Grid>
          <Grid  size={{ xs: 6, sm: 4, md: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.05em" }}>
              Date
            </Typography>
            {isEditing && scheduleEditMode ? (
              <TextField type="date" value={editedScheduleDate} onChange={(e) => setEditedScheduleDate(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
            ) : (
              <Typography variant="body2" fontWeight={500}>{formatDate(route.scheduled_date)}</Typography>
            )}
          </Grid>
          <Grid  size={{ xs: 6, sm: 4, md: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.05em" }}>
              Time Window
            </Typography>
            {isEditing && scheduleEditMode ? (
              <Box display="flex" gap={1}>
                <TextField type="time" value={editedStartTime} onChange={(e) => setEditedStartTime(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
                <TextField type="time" value={editedEndTime} onChange={(e) => setEditedEndTime(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
              </Box>
            ) : (
              <Typography variant="body2" fontWeight={500}>
                {formatTime(route.scheduled_time_start)} - {formatTime(route.scheduled_time_end)}
              </Typography>
            )}
          </Grid>
          <Grid  size={{ xs: 3, sm: 3, md: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.05em" }}>
              Orders
            </Typography>
            <Typography variant="h6" color="primary.main" fontWeight={700}>{orders.length}</Typography>
          </Grid>
          <Grid  size={{ xs: 3, sm: 3, md: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.05em" }}>
              Completed
            </Typography>
            <Typography variant="h6" color="success.main" fontWeight={700}>
              {orders.filter((o) => o.status === "completed").length}
            </Typography>
          </Grid>
          <Grid  size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.05em" }}>
              Est. Duration
            </Typography>
            <Typography variant="body2" fontWeight={500}>{orders.length * 30} min (~30 min/order)</Typography>
          </Grid>
        </Grid>
        {/* Schedule edit toggle */}
        {isEditing && (
          <Box mt={1.5} pt={1.5} borderTop="1px solid #F1F5F9">
            <FormControlLabel
              control={<Switch checked={scheduleEditMode} onChange={(e) => setScheduleEditMode(e.target.checked)} color="primary" size="small" />}
              label={<Typography variant="caption">Edit Schedule</Typography>}
            />
          </Box>
        )}
      </Paper>

      <Grid container spacing={3}>
        {/* LEFT COLUMN: Actions + Orders List */}
        <Grid  size={{ xs: 12, md: 8 }}>
          {/* Route Actions */}
          <Paper elevation={0} sx={{ p: isMobile ? 2 : 2.5, mb: 3, border: "1px solid #E2E8F0", borderRadius: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" gap={1} flexWrap="wrap">
                {route.status === "assigned" && (
                  <Button variant="contained" color="primary" onClick={() => openConfirmationDialog("start", "in_progress")} startIcon={<CarIcon />} disabled={isUpdating} size="small">
                    Start Route
                  </Button>
                )}
                {route.status === "in_progress" && (
                  <Button variant="contained" color="success" onClick={() => openConfirmationDialog("complete", "completed")} startIcon={<DoneIcon />} disabled={isUpdating} size="small">
                    Complete Route
                  </Button>
                )}
                {route.status !== "completed" && route.status !== "cancelled" && (
                  <Button variant="outlined" color="error" onClick={() => openConfirmationDialog("cancel", "cancelled")} startIcon={<DeleteIcon />} disabled={isUpdating} size="small">
                    Cancel Route
                  </Button>
                )}
                {isEditing && (
                  <Button variant="outlined" color="primary" onClick={() => setOpenAddOrdersDialog(true)} startIcon={<AddIcon />} disabled={isUpdating} size="small">
                    Add Orders
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Orders List with Drag & Drop */}
          <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 3 }}>
            <Box p={isMobile ? 2 : 3}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <OrderIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Route Orders</Typography>
                  {isEditing && (
                    <Chip label="Drag to reorder" color="info" size="small" sx={{ ml: 2 }} icon={<DragIcon />} />
                  )}
                </Box>
                {orders.length > 0 && (
                  <Chip label={`${orders.length} order${orders.length !== 1 ? "s" : ""}`} color="primary" size="small" />
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              {orders.length === 0 ? (
                <Box textAlign="center" py={5}>
                  <OrderIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                  <Typography variant="body1" color="textSecondary">No orders in this route</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Add orders to get started with route planning
                  </Typography>
                  {isEditing && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddOrdersDialog(true)}>Add Orders</Button>
                  )}
                </Box>
              ) : (
                <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                  <Droppable droppableId="route-orders">
                    {(provided, snapshot) => (
                      <List
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        sx={{
                          bgcolor: snapshot.isDraggingOver ? "action.hover" : "transparent",
                          borderRadius: 1,
                          transition: "background-color 0.2s",
                        }}
                      >
                        {reorderedOrders.map((order, index) => (
                          <Draggable key={order.id} draggableId={order.id.toString()} index={index} isDragDisabled={!isEditing || saving}>
                            {(provided, snapshot) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{ mb: 1, transition: "all 0.2s", transform: snapshot.isDragging ? "rotate(2deg)" : "none" }}
                              >
                                <Paper
                                  elevation={snapshot.isDragging ? 4 : 0}
                                  sx={{
                                    p: isMobile ? 1.5 : 2,
                                    border: snapshot.isDragging ? "2px solid" : "1px solid",
                                    borderColor: snapshot.isDragging ? "primary.main" : "#E2E8F0",
                                    borderRadius: 2,
                                    cursor: isEditing ? "grab" : "default",
                                    "&:hover": { bgcolor: "#F8FAFC" },
                                  }}
                                >
                                  <Box display="flex" alignItems="center">
                                    {isEditing && (
                                      <Box {...provided.dragHandleProps} sx={{ mr: 1.5, cursor: "grab", color: "text.secondary", "&:hover": { color: "primary.main" } }}>
                                        <DragIcon fontSize="small" />
                                      </Box>
                                    )}
                                    <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28, mr: 1.5, fontSize: "0.75rem", fontWeight: "bold" }}>
                                      {index + 1}
                                    </Avatar>
                                    <Box flex={1} minWidth={0}>
                                      <Box display="flex" alignItems="center" gap={1} mb={0.25}>
                                        <Typography variant="subtitle2" noWrap>{order.order_number}</Typography>
                                        {getStatusChip(order.status)}
                                      </Box>
                                      <Typography variant="body2" color="primary.main" noWrap>
                                        {order.patient_name || "No patient name"}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" noWrap>
                                        {order.patient_address || "No address"}
                                      </Typography>
                                    </Box>
                                    {isEditing ? (
                                      <IconButton onClick={(e) => handleOrderMenuOpen(e, order)} size="small"><MoreVertIcon /></IconButton>
                                    ) : (
                                      <Tooltip title="View Order Details">
                                        <IconButton onClick={() => navigate(`/orders/${order.id}`)} size="small" color="primary"><EditIcon fontSize="small" /></IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Paper>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                </DragDropContext>
              )}

              {saving && (
                <Box display="flex" alignItems="center" justifyContent="center" mt={2} p={1.5} bgcolor="#F0F9FF" borderRadius={2}>
                  <CircularProgress size={18} sx={{ mr: 1.5 }} />
                  <Typography variant="body2" color="primary.main">Saving route changes...</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT COLUMN: MP Assignment + Starting Point + Quick Actions */}
        <Grid  size={{ xs: 12, md: 4 }}>
          {/* Medical Professional Assignment */}
          <Paper elevation={0} sx={{ p: isMobile ? 2 : 2.5, mb: 3, border: "1px solid #E2E8F0", borderRadius: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <PersonIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={600}>Assigned Technician</Typography>
            </Box>

            {route.medical_professional_id ? (
              <>
                {assignedMP && (
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar src={assignedMP.user?.avatar_url} sx={{ mr: 2, bgcolor: "primary.main", width: 40, height: 40 }}>
                      {assignedMP.user?.full_name?.charAt(0) || "MP"}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="subtitle2" noWrap>
                        {assignedMP.user?.full_name || `Medical Professional (ID: ${assignedMP.id})`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {assignedMP.mp_category || "Not specified"}
                      </Typography>
                      {assignedMP.starting_location && (
                        <Box>
                          <Chip label="Starting point set" color="success" size="small" sx={{ mt: 0.5, height: 20, fontSize: "0.65rem" }} icon={<LocationIcon sx={{ fontSize: "14px !important" }} />} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                {!assignedMP && (
                  <Typography variant="body2" color="text.secondary">
                    Medical Professional (ID: {route.medical_professional_id})
                  </Typography>
                )}
                {(route.status === "created" || route.status === "assigned") && (
                  <Button variant="outlined" size="small" fullWidth onClick={() => setSelectedMP("")}>
                    Change Assignment
                  </Button>
                )}
              </>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No technician assigned yet.
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel id="mp-select-label">Select Technician</InputLabel>
                  <Select labelId="mp-select-label" value={selectedMP} onChange={(e) => setSelectedMP(e.target.value)} label="Select Technician" disabled={isUpdating}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {medicalProfessionals.map((mp) => (
                      <MenuItem key={mp.id} value={mp.id}>
                        <Box display="flex" alignItems="center" width="100%">
                          <Avatar src={mp.user?.avatar_url} sx={{ width: 24, height: 24, mr: 1, bgcolor: "primary.main", fontSize: "0.7rem" }}>
                            {mp.user?.full_name?.charAt(0) || "MP"}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{mp.user?.full_name || `MP ${mp.id}`}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {mp.mp_category || "Unknown"}{mp.has_starting_point ? " • Has starting point" : ""}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="contained" color="primary" fullWidth sx={{ mt: 1.5 }} onClick={handleAssignMP} disabled={!selectedMP || isUpdating} size="small">
                  {isUpdating ? "Assigning..." : "Assign Technician"}
                </Button>
              </Box>
            )}
          </Paper>

          {/* Starting Point */}
          {assignedMP && mpStartingPoint && (
            <Paper elevation={0} sx={{ p: isMobile ? 2 : 2.5, mb: 3, border: "1px solid #E2E8F0", borderRadius: 3 }}>
              <Box display="flex" alignItems="center" mb={1.5}>
                <HomeIcon color="success" sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={600}>Starting Point</Typography>
                <Chip label="Optimized" color="success" size="small" sx={{ ml: "auto", height: 20, fontSize: "0.65rem" }} />
              </Box>
              <Box display="flex" alignItems="center" p={1.5} bgcolor="#F0FDF4" borderRadius={2}>
                <Avatar sx={{ bgcolor: "success.main", mr: 1.5, width: 32, height: 32 }}>
                  {mpStartingPoint.type === "home" ? <HomeIcon fontSize="small" /> : <BusinessIcon fontSize="small" />}
                </Avatar>
                <Box minWidth={0}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {mpStartingPoint.name || mpStartingPoint.address}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {mpStartingPoint.address}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Quick Actions */}
          <Paper elevation={0} sx={{ p: isMobile ? 2 : 2.5, mb: 3, border: "1px solid #E2E8F0", borderRadius: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Quick Actions</Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <Button size="small" variant="outlined" fullWidth startIcon={<MapIcon />} onClick={handleOpenInMaps} disabled={orders.length === 0}>
                Open in Google Maps
              </Button>
              <Button size="small" variant="outlined" fullWidth startIcon={<CarIcon />} onClick={handleGetDirections} disabled={orders.length === 0}>
                Get Directions
              </Button>
              <Button size="small" variant="outlined" fullWidth startIcon={<ReorderIcon />} onClick={handleStartEdit} disabled={isEditing}>
                Reorder Stops
              </Button>
              <Button size="small" variant="outlined" fullWidth startIcon={<ScheduleIcon />}
                onClick={() => { setIsEditing(true); setScheduleEditMode(true); }}
                disabled={isEditing || route.status === "completed" || route.status === "cancelled"}
              >
                Edit Schedule
              </Button>
            </Box>
          </Paper>

          {/* Optimization Tips */}
          {assignedMP && !mpStartingPoint && (
            <Alert severity="warning" sx={{ borderRadius: 3, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Optimization Opportunity</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Configure a starting point for {assignedMP.user?.full_name || "this technician"} to get better route optimization.
              </Typography>
              <Button size="small" variant="outlined" onClick={() => navigate(`/medical-professionals/${assignedMP.id}/edit`)}>
                Configure Starting Point
              </Button>
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* Route Map - Full Width */}
      {orders.length > 0 && (
        <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, mt: 3, border: "1px solid #E2E8F0", borderRadius: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <MapIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Route Map</Typography>
              <Chip label={`${orders.length} stops`} size="small" sx={{ ml: 1.5, height: 22, fontSize: "0.7rem", bgcolor: "#EFF6FF", color: "#0369A1", fontWeight: 600 }} />
            </Box>
            <Box display="flex" gap={1}>
              <Button size="small" variant="outlined" startIcon={<MapIcon />} onClick={handleOpenInMaps}>
                Google Maps
              </Button>
              <Button size="small" variant="contained" startIcon={<CarIcon />} onClick={handleGetDirections}>
                Directions
              </Button>
            </Box>
          </Box>
          <RouteMap
            routes={[
              {
                name: route.name,
                orders: orders,
                starting_point: mpStartingPoint
                  ? {
                      lat: mpStartingPoint.latitude,
                      lng: mpStartingPoint.longitude,
                      name: mpStartingPoint.address || "Starting Point",
                    }
                  : null,
                medical_professional_name: assignedMP?.user?.full_name || "",
              },
            ]}
            height={isMobile ? "300px" : "450px"}
            singleRouteColor="#0369A1"
          />
        </Paper>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {dialogAction?.action === "start" && "Start Route"}
          {dialogAction?.action === "complete" && "Complete Route"}
          {dialogAction?.action === "cancel" && "Cancel Route"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogAction?.action === "start" &&
              "Are you sure you want to start this route? This will mark the route as in progress."}
            {dialogAction?.action === "complete" &&
              "Are you sure you want to mark this route as completed? All orders in this route will be marked as completed."}
            {dialogAction?.action === "cancel" &&
              "Are you sure you want to cancel this route? This action cannot be undone."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>No, Cancel</Button>
          <Button
            onClick={() => handleUpdateStatus(dialogAction?.status)}
            color={
              dialogAction?.action === "cancel"
                ? "error"
                : dialogAction?.action === "complete"
                ? "success"
                : "primary"
            }
            variant="contained"
            autoFocus
          >
            Yes, Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Actions Menu */}
      <Menu
        anchorEl={orderActionsAnchorEl}
        open={Boolean(orderActionsAnchorEl)}
        onClose={handleOrderMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleOrderMenuClose();
            if (selectedOrder) navigate(`/orders/${selectedOrder.id}`);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Order Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRemoveOrderFromRoute}>
          <ListItemIcon>
            <RemoveIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Remove from Route</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Orders Dialog */}
      <Dialog
        open={openAddOrdersDialog}
        onClose={() => setOpenAddOrdersDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Orders to Route</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Search orders"
            type="text"
            fullWidth
            variant="outlined"
            value={orderSearchQuery}
            onChange={(e) => setOrderSearchQuery(e.target.value)}
            placeholder="Search by order number, patient name, or address"
            sx={{ mb: 2 }}
          />

          {availableOrders.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
              No available orders for this date. Create orders first.
            </Typography>
          ) : filteredAvailableOrders.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
              No orders match your search.
            </Typography>
          ) : (
            <List sx={{ pt: 0 }}>
              {filteredAvailableOrders.map((order) => (
                <ListItem
                  key={order.id}
                  disablePadding
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      checked={selectedAvailableOrders.includes(order.id)}
                      onChange={() => handleToggleSelectOrder(order.id)}
                    />
                  }
                >
                  <ListItemButton
                    onClick={() => handleToggleSelectOrder(order.id)}
                  >
                    <ListItemIcon>
                      <LocationIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={order.order_number}
                      secondary={
                        <>
                          <Typography component="span" variant="body2">
                            {order.patient?.first_name}{" "}
                            {order.patient?.last_name}
                          </Typography>
                          <Typography
                            component="div"
                            variant="caption"
                            color="textSecondary"
                          >
                            {order.patient?.address || "No address"}
                            {order.patient?.city && `, ${order.patient.city}`}
                            {order.patient?.state && `, ${order.patient.state}`}
                            {order.patient?.zip_code &&
                              ` ${order.patient.zip_code}`}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            px={2}
          >
            <Typography variant="body2">
              {selectedAvailableOrders.length} orders selected
            </Typography>
            <Box>
              <Button onClick={() => setOpenAddOrdersDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setOpenAddOrdersDialog(false);
                  handleSaveRouteChanges();
                }}
                variant="contained"
                color="primary"
                disabled={selectedAvailableOrders.length === 0}
              >
                Add Selected Orders
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RouteDetail;
